import { FunctionComponent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/types/navigation';
import { QuestActivityType } from 'src/enums/quests';
import { QuestActivity } from 'src/types/quests';
import {
    TaskSummary,
    getTaskStatusLabel,
    getTaskStatusColors
} from 'src/types/task';
import {
    SectionWrapper,
    SectionTitle,
    IterationGroup,
    IterationLabel,
    TaskList,
    TaskRowItem,
    TaskRowTitle,
    TaskStatusPill
} from './styled';

interface Props {
    tasks: TaskSummary[];
    activities: QuestActivity[];
}

function getOrdinal(n: number): string {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
}

function buildTaskNumberMap(
    activities: QuestActivity[],
    tasks: TaskSummary[]
): Record<string, number> {
    const map: Record<string, number> = {};
    const taskById = new Map(tasks.map((t) => [t.id, t]));
    const breakdowns = activities
        .filter(
            (a) =>
                a.activityType === QuestActivityType.QUEST_BREAKDOWN_SUBMITTED
        )
        .sort((a, b) => a.created.localeCompare(b.created));

    const iterationCounters = new Map<number, number>();

    for (const activity of breakdowns) {
        const details = activity.details as {
            taskIds?: string[];
            tasksToCreate?: string[];
        };
        const taskIds = details?.taskIds;

        if (taskIds) {
            for (const id of taskIds) {
                if (!(id in map)) {
                    const iteration = taskById.get(id)?.iteration ?? 0;
                    const counter = (iterationCounters.get(iteration) ?? 0) + 1;
                    iterationCounters.set(iteration, counter);
                    map[id] = counter;
                }
            }
        } else {
            const titles = details?.tasksToCreate ?? [];
            for (const title of titles) {
                const task = tasks.find(
                    (t) => t.title === title && !(t.id in map)
                );
                if (task) {
                    const iteration = task.iteration ?? 0;
                    const counter = (iterationCounters.get(iteration) ?? 0) + 1;
                    iterationCounters.set(iteration, counter);
                    map[task.id] = counter;
                }
            }
        }
    }
    return map;
}

const TasksSection: FunctionComponent<Props> = ({ tasks, activities }) => {
    const navigate = useNavigate();
    const taskNumberMap = useMemo(
        () => buildTaskNumberMap(activities, tasks),
        [activities, tasks]
    );

    const iterationGroups = useMemo(() => {
        const groups: Record<number, TaskSummary[]> = {};
        tasks.forEach((task) => {
            const iter = task.iteration ?? 0;
            (groups[iter] ??= []).push(task);
        });
        return Object.entries(groups)
            .map(([key, value]) => ({
                iteration: parseInt(key),
                tasks: value.sort(
                    (a, b) =>
                        (taskNumberMap[a.id] ?? Infinity) -
                        (taskNumberMap[b.id] ?? Infinity)
                )
            }))
            .sort((a, b) => a.iteration - b.iteration);
    }, [tasks, taskNumberMap]);

    if (tasks.length === 0) return null;

    return (
        <SectionWrapper>
            <SectionTitle>Tasks</SectionTitle>

            {iterationGroups.map(({ iteration, tasks: iterTasks }) => (
                <IterationGroup key={iteration}>
                    <IterationLabel>
                        {iteration > 0
                            ? `${getOrdinal(iteration)} iteration`
                            : 'Tasks'}
                    </IterationLabel>

                    <TaskList>
                        {iterTasks.map((task) => {
                            const { bg, textColor } = getTaskStatusColors(
                                task.status
                            );
                            const num = taskNumberMap[task.id];
                            return (
                                <TaskRowItem
                                    key={task.id}
                                    onClick={() =>
                                        navigate(
                                            RoutesEnum.TASK_DETAIL.replace(
                                                ':id',
                                                task.id
                                            )
                                        )
                                    }
                                >
                                    <TaskRowTitle>
                                        {num ? `#${num} ` : ''}
                                        {task.title}
                                    </TaskRowTitle>
                                    <TaskStatusPill
                                        pillbg={bg}
                                        pillcolor={textColor}
                                    >
                                        {getTaskStatusLabel(task.status)}
                                    </TaskStatusPill>
                                </TaskRowItem>
                            );
                        })}
                    </TaskList>
                </IterationGroup>
            ))}
        </SectionWrapper>
    );
};

export default TasksSection;
